/**
 * useTone Hook - Manages native Web Audio API lifecycle and operations
 * Provides React integration for NativeAudioService
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { nativeAudioService } from '../services/audio/NativeAudioService';
import type { InstrumentType } from '../types/composition';
import type { ADSRSettings } from '../services/audio/ADSREnvelope';
import type { InstrumentParameters } from '../types/instruments';
/**
 * Step change callback type
 */
export type StepChangeCallback = (step: number) => void;
/**
 * Return type for useTone hook
 */
export interface UseToneReturn {
    /** Whether Tone.js is initialized and ready */
    isReady: boolean;
    /** Start playback */
    play: () => Promise<void>;
    /** Pause playback */
    pause: () => void;
    /** Stop playback */
    stop: () => void;
    /** Set BPM */
    setBpm: (bpm: number) => void;
    /** Set loop mode */
    setLoop: (enabled: boolean) => void;
    /** Set volume for a specific row */
    setVolume: (row: number, volume: number) => void;
    /** Mute/unmute a specific row */
    setMute: (row: number, muted: boolean) => void;
    /** Update parameters for a specific row */
    updateVoiceParameters: (row: number, parameters: InstrumentParameters) => void;
    /** Export audio as WAV blob */
    exportAudio: () => Promise<Blob>;
    /** Get current transport state */
    getState: () => 'started' | 'paused' | 'stopped';
}
/**
 * Props for useTone hook
 */
export interface UseToneProps {
    /** Array of instrument types */
    instrumentTypes: InstrumentType[];
    /** Grid state (active cells) */
    gridState: boolean[][];
    /** Note state (pitches) */
    noteState: (string | null)[][];
    /** Number of steps */
    steps: number;
    /** Beats per minute */
    bpm: number;
    /** Optional ADSR settings per instrument (legacy support) */
    adsrSettings?: ADSRSettings[];
    /** Optional instrument parameters per instrument */
    instrumentParameters?: (InstrumentParameters | undefined)[];
    /** Optional array of flags indicating whether to apply parameters per instrument */
    parametersEnabled?: boolean[];
    /** Callback when step changes during playback */
    onStepChange?: StepChangeCallback;
}
/**
 * Custom hook for managing Tone.js integration
 *
 * @param props - Hook configuration
 * @returns Tone.js control methods and state
 */
export function useTone({ instrumentTypes, gridState, noteState, steps, bpm, adsrSettings, instrumentParameters, parametersEnabled, onStepChange, }: UseToneProps): UseToneReturn {
    const [isReady, setIsReady] = useState(false);
    const isInitializedRef = useRef(false);
    /**
     * Initialize Tone.js on mount
     */
    useEffect(() => {
        // Don't auto-initialize - wait for user interaction
        // This is handled in the play() method
        return () => {
            // Clean up on unmount
            nativeAudioService.dispose();
            isInitializedRef.current = false;
            setIsReady(false);
        };
    }, []);
    /**
     * Track previous state to detect when voices/sequencer need updates
     */
    const prevInstrumentsRef = useRef<string>('');
    const prevParametersEnabledRef = useRef<string>('');
    const prevParametersRef = useRef<string>('');
    /**
     * Recreate voices only when instrument types or parametersEnabled changes
     * IMPORTANT: Stop playback first to prevent timing issues
     */
    useEffect(() => {
        if (isReady && instrumentTypes.length > 0) {
            // Serialize current state for comparison
            const currentInstruments = JSON.stringify(instrumentTypes);
            const currentParametersEnabled = JSON.stringify(parametersEnabled);
            // Check if we need to recreate voices
            const needsVoiceRecreation = currentInstruments !== prevInstrumentsRef.current ||
                currentParametersEnabled !== prevParametersEnabledRef.current;
            if (needsVoiceRecreation) {
                // Get current transport state
                const wasPlaying = nativeAudioService.getState() === 'started';
                // Stop playback to prevent timing issues during voice recreation
                if (wasPlaying) {
                    nativeAudioService.stop();
                }
                // Recreate voices with new instruments and parameters (async)
                (async () => {
                    await nativeAudioService.createVoices(instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled);
                    // Reinitialize sequencer with new voices
                    nativeAudioService.initSequencer(gridState, noteState, steps, bpm, onStepChange);
                    // Resume playback if it was playing
                    if (wasPlaying) {
                        // Use setTimeout to ensure voice creation is complete
                        setTimeout(() => {
                            nativeAudioService.play().catch(err => {
                                console.error('Failed to resume playback after voice recreation:', err);
                            });
                        }, 50);
                    }
                    // Update refs
                    prevInstrumentsRef.current = currentInstruments;
                    prevParametersEnabledRef.current = currentParametersEnabled;
                })();
            }
        }
    }, [instrumentTypes, parametersEnabled, isReady, gridState, noteState, steps, bpm, onStepChange, adsrSettings, instrumentParameters]);
    /**
     * Update voice parameters in real-time when only parameters change
     * This avoids recreating voices and maintains smooth playback
     */
    useEffect(() => {
        if (isReady && instrumentParameters && instrumentParameters.length > 0) {
            const currentParameters = JSON.stringify(instrumentParameters);
            // Check if only parameters changed (not instruments or enabled flags)
            if (currentParameters !== prevParametersRef.current) {
                // Update parameters for each voice without recreating
                instrumentParameters.forEach((params, row) => {
                    if (params && parametersEnabled?.[row]) {
                        nativeAudioService.updateVoiceParameters(row, params);
                    }
                });
                prevParametersRef.current = currentParameters;
            }
        }
    }, [instrumentParameters, parametersEnabled, isReady]);
    /**
     * Update sequencer when grid/notes change.
     * During playback, update grid state in-place so changes are heard immediately.
     * When stopped, do a full re-init.
     */
    useEffect(() => {
        if (isReady && instrumentTypes.length > 0) {
            const transportState = nativeAudioService.getState();
            if (transportState === 'started') {
                // Hot-update grid/note state without interrupting playback
                nativeAudioService.updateGridState(gridState, noteState);
            }
            else {
                nativeAudioService.initSequencer(gridState, noteState, steps, bpm, onStepChange);
            }
        }
    }, [gridState, noteState, steps, bpm, isReady, onStepChange]);
    /**
     * Play audio
     */
    const play = useCallback(async () => {
        try {
            // Initialize on first play
            if (!isReady) {
                await nativeAudioService.initialize();
                await nativeAudioService.createVoices(instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled);
                nativeAudioService.initSequencer(gridState, noteState, steps, bpm, onStepChange);
                // Update refs so the voice recreation effect doesn't double-create
                prevInstrumentsRef.current = JSON.stringify(instrumentTypes);
                prevParametersEnabledRef.current = JSON.stringify(parametersEnabled);
                prevParametersRef.current = JSON.stringify(instrumentParameters);
                setIsReady(true);
                isInitializedRef.current = true;
            }
            await nativeAudioService.play();
        }
        catch (error) {
            console.error('Failed to start playback', error);
            throw error;
        }
    }, [isReady, instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled, gridState, noteState, steps, bpm, onStepChange]);
    /**
     * Pause audio
     */
    const pause = useCallback(() => {
        nativeAudioService.pause();
    }, []);
    /**
     * Stop audio
     */
    const stop = useCallback(() => {
        nativeAudioService.stop();
    }, []);
    /**
     * Set BPM
     */
    const setBpm = useCallback((newBpm: number) => {
        nativeAudioService.setBpm(newBpm);
    }, []);
    /**
     * Set loop mode
     */
    const setLoop = useCallback((enabled: boolean) => {
        nativeAudioService.setLoop(enabled);
    }, []);
    /**
     * Set volume for a row
     */
    const setVolume = useCallback((row: number, volume: number) => {
        nativeAudioService.setVolume(row, volume);
    }, []);
    /**
     * Mute/unmute a row
     */
    const setMute = useCallback((row: number, muted: boolean) => {
        nativeAudioService.setMute(row, muted);
    }, []);
    /**
     * Update voice parameters for a row
     */
    const updateVoiceParameters = useCallback((row: number, parameters: InstrumentParameters) => {
        nativeAudioService.updateVoiceParameters(row, parameters);
    }, []);
    /**
     * Export audio by recording one complete playback
     * This plays the composition once and records the exact audio output
     */
    const exportAudio = useCallback(async (): Promise<Blob> => {
        try {
            // Initialize audio system if not ready (same as play function)
            if (!isReady) {
                await nativeAudioService.initialize();
                await nativeAudioService.createVoices(instrumentTypes, adsrSettings, instrumentParameters, parametersEnabled);
                nativeAudioService.initSequencer(gridState, noteState, steps, bpm, onStepChange);
                // Update refs so the voice recreation effect doesn't double-create
                prevInstrumentsRef.current = JSON.stringify(instrumentTypes);
                prevParametersEnabledRef.current = JSON.stringify(parametersEnabled);
                prevParametersRef.current = JSON.stringify(instrumentParameters);
                setIsReady(true);
                isInitializedRef.current = true;
            }
            // Now check if we have what we need
            const audioContext = nativeAudioService.getAudioContext();
            const masterGain = nativeAudioService.getMasterGain();
            if (!audioContext || !masterGain) {
                throw new Error('Failed to initialize audio system for export');
            }
            // Import the playback recorder
            const { PlaybackRecorder } = await import('../services/audio/PlaybackRecorder');
            const { AudioExportService } = await import('../services/audio/AudioExportService');
            // Calculate duration for one complete pattern
            const duration = AudioExportService.calculateDuration(steps, bpm, 1);
            console.log('[useTone] Recording one playthrough, duration:', duration, 'seconds');
            const recorder = new PlaybackRecorder();
            return await recorder.recordPlayback(audioContext, masterGain, duration, async () => {
                nativeAudioService.stop(); // Ensure we start from beginning
                await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
                await nativeAudioService.play();
            }, () => {
                nativeAudioService.stop();
            });
        }
        catch (error) {
            console.error('[useTone] Export failed:', error);
            throw error;
        }
    }, [
        isReady,
        instrumentTypes,
        adsrSettings,
        instrumentParameters,
        parametersEnabled,
        gridState,
        noteState,
        steps,
        bpm,
        onStepChange
    ]);
    /**
     * Get current transport state
     */
    const getState = useCallback(() => {
        return nativeAudioService.getState();
    }, []);
    return {
        isReady,
        play,
        pause,
        stop,
        setBpm,
        setLoop,
        setVolume,
        setMute,
        updateVoiceParameters,
        exportAudio,
        getState,
    };
}
